import { el } from '../../core/dom.js';
import {
  connectorSummary,
  createdLabel,
  initials,
  planBillingLine,
  sessionDay,
  sessionTitle,
  statusPill,
  statusTone,
  statusWord,
  subline,
  unreadLabel,
} from './model.js';

function tabs(tab, unread, onTab) {
  const items = [
    { id: 'overview', label: 'Overview' },
    { id: 'messages', label: 'Messages', badge: unread },
    { id: 'sessions', label: 'Sessions' },
  ];
  return el('div', { class: 'org-tabs', role: 'tablist', 'aria-label': 'Organization sections' },
    items.map((item) => el('button', {
      type: 'button',
      role: 'tab',
      class: tab === item.id ? 'org-tab is-on' : 'org-tab',
      'aria-selected': tab === item.id ? 'true' : 'false',
      onClick: () => onTab(item.id),
    },
    item.label,
    item.badge ? el('span', { class: 'org-unread' }, item.badge) : null)));
}

function overview(tenant) {
  const primary = (tenant && tenant.primary_contact) || {};
  const health = connectorSummary(tenant);
  const tone = statusTone(tenant);
  return el('div', { class: 'org-overview' },
    el('div', { class: 'org-stats' },
      stat('Slug', el('span', { class: 'org-mono' }, tenant.slug || '—')),
      stat('Users', el('span', { class: 'org-metric' }, String(tenant.user_count || 0))),
      stat('Status', el('span', { class: `org-status is-${tone}` },
        tone === 'active' ? el('span', { class: 'org-pulse', 'aria-hidden': 'true' }) : null,
        el('em', {}, statusWord(tenant)))),
      stat('Created', el('span', { class: 'org-mono' }, createdLabel(tenant.created_at)))),
    el('div', { class: 'org-cards' },
      el('div', { class: 'org-lift' },
        el('span', { class: 'org-lbl' }, 'Connector health'),
        el('p', { class: 'org-card-lead' },
          health.lead,
          health.accent ? el('em', {}, health.accent) : null)),
      el('div', { class: 'org-soft' },
        el('span', { class: 'org-lbl' }, 'Plan and billing'),
        el('p', { class: 'org-card-lead' }, planBillingLine(tenant)))),
    el('div', { class: 'org-contact' },
      el('div', { class: 'org-contact-ident' },
        el('span', { class: 'org-contact-av', 'aria-hidden': 'true' }, initials(primary.name || tenant.name)),
        el('span', { class: 'org-contact-copy' },
          el('span', { class: 'org-lbl' }, 'Primary contact'),
          el('span', { class: 'org-contact-name' }, primary.name || '—'),
          primary.email ? el('span', { class: 'org-contact-email' }, primary.email) : null)),
      primary.email
        ? el('a', { class: 'org-btn', href: `mailto:${primary.email}` }, 'Email contact')
        : null));
}

function stat(label, value) {
  return el('div', { class: 'org-stat' },
    el('span', { class: 'org-lbl' }, label),
    value);
}

function messagesPane(state, handlers) {
  const rows = state.messages || [];
  return el('div', { class: 'org-thread' },
    el('div', { class: 'org-thread-log', id: 'org-thread-log' },
      rows.length
        ? rows.map((msg) => el('div', {
          class: msg.role === 'admin' ? 'org-bubble is-admin' : 'org-bubble is-user',
        }, msg.text || ''))
        : el('p', { class: 'org-thread-empty' }, 'No messages yet.')),
    el('form', {
      class: 'org-compose',
      onSubmit: (event) => {
        event.preventDefault();
        const input = event.currentTarget.querySelector('input');
        const text = input ? input.value : '';
        handlers.onSend(text, input);
      },
    },
    el('input', {
      class: 'org-input',
      type: 'text',
      maxlength: '4000',
      placeholder: 'Reply to this organization…',
      autocomplete: 'off',
      disabled: state.sending ? true : null,
    }),
    el('button', {
      type: 'submit',
      class: 'org-send',
      disabled: state.sending ? true : null,
    }, 'Send')));
}

function sessionsPane(sessions) {
  const rows = sessions || [];
  if (!rows.length) {
    return el('div', { class: 'org-sessions' },
      el('p', { class: 'org-thread-empty' }, 'No sessions booked yet.'));
  }
  return el('div', { class: 'org-sessions' },
    rows.map((row) => el('div', { class: 'org-session' },
      el('span', { class: 'org-session-day' }, sessionDay(row)),
      el('span', { class: 'org-session-copy' },
        el('span', { class: 'org-session-title' }, sessionTitle(row)),
        el('span', { class: 'org-session-sub' },
          `Booked by ${row.booked_by_name || row.booked_by_email || 'Unknown'}`)),
      el('span', { class: 'org-pill' }, statusPill(row.status)))));
}

export function renderEmptyDetail() {
  return el('div', { class: 'org-empty-detail' },
    el('p', { class: 'kicker' }, 'Nothing here'),
    el('h2', {}, 'Select an organization'),
    el('p', {}, 'Choose one from the list to view messages, sessions, and account details.'));
}

export function renderDetail(state, handlers) {
  const tenant = state.selectedTenant;
  if (!tenant) {
    if (state.detailLoading) {
      return el('div', { class: 'org-detail is-loading' }, el('p', {}, 'Loading organization…'));
    }
    return renderEmptyDetail();
  }
  const unread = unreadLabel(tenant.unread_messages);
  let body;
  if (state.detailLoading) body = el('p', { class: 'org-thread-empty' }, 'Loading…');
  else if (state.tab === 'messages') body = messagesPane(state, handlers);
  else if (state.tab === 'sessions') body = sessionsPane(state.sessions);
  else body = overview(tenant);

  return el('section', { class: 'org-detail', 'aria-label': tenant.name || 'Organization' },
    el('div', { class: 'org-detail-head' },
      el('div', { class: 'org-detail-ident' },
        el('span', { class: 'org-detail-av', 'aria-hidden': 'true' }, initials(tenant.name)),
        el('div', {},
          el('h2', { class: 'org-detail-name' },
            tenant.name || 'Organization',
            unread ? el('span', { class: 'org-unread' }, unread) : null),
          el('p', { class: 'org-detail-sub' }, subline(tenant)))),
      tabs(state.tab, unread, handlers.onTab)),
    body);
}
